import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { personalAdvisorApi, AdvisorRequestForm, AdvisorResponseData, scoutApi, ScoutVerdict } from '../../services/api';
import StructureChart from './StructureChart';
import './Advisor.css';

type Instrument = 'NATURALGAS' | 'OIL_CRUDE' | 'US100';
type Direction = 'BUY' | 'SELL';
type Tab = 'advisor' | 'scout' | 'structure';

const INSTRUMENT_LABELS: Record<Instrument, string> = {
  NATURALGAS: 'Natural Gas',
  OIL_CRUDE: 'Crude Oil (WTI)',
  US100: 'Nasdaq 100',
};

const Advisor: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('structure');

  // ─── Advisor (multi-week) state ─────────────────────────────────────
  const [instrument, setInstrument] = useState<Instrument>('NATURALGAS');
  const [direction, setDirection] = useState<Direction>('BUY');
  const [hasPosition, setHasPosition] = useState(false);
  const [positionSide, setPositionSide] = useState<Direction>('BUY');
  const [entryPrice, setEntryPrice] = useState<string>('');
  const [openedAt, setOpenedAt] = useState<string>('');
  const [pdf, setPdf] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<AdvisorResponseData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [uploadingOnly, setUploadingOnly] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ ok: boolean; message: string } | null>(null);

  // ─── Scout (experimental NG) state ──────────────────────────────────
  const [scoutRunning, setScoutRunning] = useState(false);
  const [scoutElapsed, setScoutElapsed] = useState(0);
  const [scoutResult, setScoutResult] = useState<ScoutVerdict | null>(null);
  const [scoutError, setScoutError] = useState<string | null>(null);

  // Wait-timer for advisor
  React.useEffect(() => {
    if (!submitting) return;
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [submitting]);

  // Wait-timer for scout
  React.useEffect(() => {
    if (!scoutRunning) return;
    const start = Date.now();
    const id = setInterval(() => setScoutElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [scoutRunning]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setElapsed(0);
    setResult(null);
    setError(null);
    try {
      const req: AdvisorRequestForm = {
        instrument,
        direction,
        hasPosition,
        positionSide: hasPosition ? positionSide : undefined,
        entryPrice: hasPosition && entryPrice !== '' ? Number(entryPrice) : undefined,
        openedAt: hasPosition && openedAt !== '' ? openedAt : undefined,
        pdf: instrument === 'NATURALGAS' ? pdf : null,
      };
      const r = await personalAdvisorApi.analyze(req);
      setResult(r);
    } catch (err: any) {
      console.error('advisor error', err);
      setError(err?.response?.data?.detail || err?.message || 'Unknown error');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setPdf(f);
    setUploadStatus(null);
  };

  const handleUploadOnly = async () => {
    if (!pdf) return;
    setUploadingOnly(true);
    setUploadStatus(null);
    try {
      const r = await personalAdvisorApi.uploadNatGasReport(pdf);
      setUploadStatus({
        ok: true,
        message: `Saved ${r.report_date} report (${r.extracted_field_count} fields extracted, ${r.raw_text_len} chars). The intraday analyst will use it on the next NG signal.`,
      });
    } catch (err: any) {
      console.error('upload error', err);
      setUploadStatus({
        ok: false,
        message: err?.response?.data?.detail || err?.message || 'Upload failed',
      });
    } finally {
      setUploadingOnly(false);
    }
  };

  const handleRunScout = async () => {
    setScoutRunning(true);
    setScoutElapsed(0);
    setScoutResult(null);
    setScoutError(null);
    try {
      const r = await scoutApi.runNow();
      setScoutResult(r);
    } catch (err: any) {
      console.error('scout error', err);
      setScoutError(err?.response?.data?.detail || err?.message || 'Unknown error');
    } finally {
      setScoutRunning(false);
    }
  };

  return (
    <div className={`advisor-page ${activeTab === 'structure' ? 'advisor-page--wide' : ''}`}>
      <header className="advisor-header">
        <h1>Personal Advisor</h1>
        <p className="advisor-subtitle">
          Multi-week positioning (Advisor) and on-demand intraday structural reads (Scout).
        </p>
      </header>

      <div className="advisor-tabs">
        <button
          type="button"
          className={`advisor-tab ${activeTab === 'advisor' ? 'active' : ''}`}
          onClick={() => setActiveTab('advisor')}
        >
          Advisor (multi-week)
        </button>
        <button
          type="button"
          className={`advisor-tab ${activeTab === 'scout' ? 'active' : ''}`}
          onClick={() => setActiveTab('scout')}
        >
          Scout (NG intraday)
        </button>
        <button
          type="button"
          className={`advisor-tab ${activeTab === 'structure' ? 'active' : ''}`}
          onClick={() => setActiveTab('structure')}
        >
          Structure Trainer
        </button>
      </div>

      {activeTab === 'advisor' && (
        <>
          <form className="advisor-form" onSubmit={handleSubmit}>
            <div className="advisor-form-row">
              <label className="advisor-field">
                <span className="advisor-label">Instrument</span>
                <select
                  className="advisor-input"
                  value={instrument}
                  onChange={(e) => setInstrument(e.target.value as Instrument)}
                  disabled={submitting}
                >
                  {(Object.keys(INSTRUMENT_LABELS) as Instrument[]).map((i) => (
                    <option key={i} value={i}>{INSTRUMENT_LABELS[i]}</option>
                  ))}
                </select>
              </label>

              <fieldset className="advisor-field">
                <legend className="advisor-label">Direction you're considering</legend>
                <div className="advisor-radio-row">
                  <label className={`advisor-radio ${direction === 'BUY' ? 'active buy' : ''}`}>
                    <input type="radio" value="BUY" checked={direction === 'BUY'}
                           onChange={() => setDirection('BUY')} disabled={submitting} />
                    BUY (long)
                  </label>
                  <label className={`advisor-radio ${direction === 'SELL' ? 'active sell' : ''}`}>
                    <input type="radio" value="SELL" checked={direction === 'SELL'}
                           onChange={() => setDirection('SELL')} disabled={submitting} />
                    SELL (short)
                  </label>
                </div>
              </fieldset>
            </div>

            <fieldset className="advisor-field advisor-position-block">
              <legend className="advisor-label">
                <label className="advisor-toggle">
                  <input type="checkbox" checked={hasPosition}
                         onChange={(e) => setHasPosition(e.target.checked)} disabled={submitting} />
                  I already have a position in this instrument
                </label>
              </legend>
              {hasPosition && (
                <div className="advisor-position-fields">
                  <label className="advisor-field-inline">
                    <span>Side</span>
                    <select className="advisor-input"
                            value={positionSide}
                            onChange={(e) => setPositionSide(e.target.value as Direction)}
                            disabled={submitting}>
                      <option value="BUY">Long (BUY)</option>
                      <option value="SELL">Short (SELL)</option>
                    </select>
                  </label>
                  <label className="advisor-field-inline">
                    <span>Entry price</span>
                    <input className="advisor-input" type="number" step="any" inputMode="decimal"
                           value={entryPrice}
                           onChange={(e) => setEntryPrice(e.target.value)}
                           placeholder="e.g. 2.85"
                           disabled={submitting} />
                  </label>
                  <label className="advisor-field-inline">
                    <span>Opened at (optional)</span>
                    <input className="advisor-input" type="text"
                           value={openedAt}
                           onChange={(e) => setOpenedAt(e.target.value)}
                           placeholder="e.g. 2 weeks ago, 2026-04-15"
                           disabled={submitting} />
                  </label>
                </div>
              )}
            </fieldset>

            {instrument === 'NATURALGAS' && (
              <fieldset className="advisor-field">
                <legend className="advisor-label">
                  NatGas Central daily PDF (optional)
                </legend>
                <p className="advisor-hint">
                  Upload today's report. <strong>Save report only</strong> stores it for the intraday analyst
                  to use on every NG signal — no Claude call, no cost. Or attach it here and it'll also
                  be picked up by the analysis when you click <strong>Run analysis</strong> below.
                </p>
                <input className="advisor-file-input" type="file" accept=".pdf,application/pdf"
                       onChange={handlePdfChange} disabled={submitting || uploadingOnly} />
                {pdf && (
                  <div className="advisor-file-row">
                    <div className="advisor-file-name">📄 {pdf.name} ({Math.round(pdf.size / 1024)} KB)</div>
                    <button
                      type="button"
                      className="advisor-upload-btn"
                      disabled={!pdf || uploadingOnly || submitting}
                      onClick={handleUploadOnly}
                    >
                      {uploadingOnly ? 'Saving…' : 'Save report only'}
                    </button>
                  </div>
                )}
                {uploadStatus && (
                  <div className={`advisor-upload-status ${uploadStatus.ok ? 'ok' : 'err'}`}>
                    {uploadStatus.ok ? '✓ ' : '✗ '}
                    {uploadStatus.message}
                  </div>
                )}
              </fieldset>
            )}

            <div className="advisor-actions">
              <button className="advisor-submit" type="submit" disabled={submitting}>
                {submitting ? `Analyzing... ${elapsed}s` : 'Run analysis'}
              </button>
              {submitting && (
                <span className="advisor-hint">
                  Typical run: 1.5–3 minutes (the agent calls 5–10 tools and writes the analysis).
                </span>
              )}
            </div>
          </form>

          {error && (
            <div className="advisor-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          {result && (
            <article className="advisor-result">
              <header className="advisor-result-meta">
                <div>
                  <strong>{INSTRUMENT_LABELS[result.instrument as Instrument] || result.instrument}</strong>
                  {' '}— {result.direction}
                </div>
                <div className="advisor-result-stats">
                  <span>Tools: {result.tools_used}</span>
                  <span>Latency: {(result.latency_ms / 1000).toFixed(1)}s</span>
                  {result.used_uploaded_report && result.report_date && (
                    <span>Used PDF: {result.report_date}</span>
                  )}
                </div>
              </header>
              <div className="advisor-markdown">
                <ReactMarkdown>{result.analysis_markdown}</ReactMarkdown>
              </div>
            </article>
          )}
        </>
      )}

      {activeTab === 'scout' && (
        <div className="scout-tab">
          <p className="advisor-hint">
            On-demand run of the experimental NATURALGAS scout — the same agent the scheduled timer
            calls hourly. <strong>Read-only:</strong> returns the verdict but does NOT place a trade.
            The scheduled timer is the only path that actually places.
          </p>

          <div className="advisor-actions">
            <button
              className="advisor-submit"
              type="button"
              onClick={handleRunScout}
              disabled={scoutRunning}
            >
              {scoutRunning ? `Scanning... ${scoutElapsed}s` : 'Run scout now'}
            </button>
            {scoutRunning && (
              <span className="advisor-hint">
                Typical run: 1.5–3 minutes (the agent calls 8–15 tools).
              </span>
            )}
          </div>

          {scoutError && (
            <div className="advisor-error">
              <strong>Error:</strong> {scoutError}
            </div>
          )}

          {scoutResult && (
            <article className="advisor-result">
              <header className="advisor-result-meta">
                <div className="scout-verdict-row">
                  <span className={`scout-action-badge ${scoutResult.action === 'TRADE' ? 'trade' : 'pass'}`}>
                    {scoutResult.action}
                  </span>
                  {scoutResult.direction && (
                    <span className={`scout-dir-badge ${scoutResult.direction === 'BUY' ? 'buy' : 'sell'}`}>
                      {scoutResult.direction}
                    </span>
                  )}
                  {scoutResult.setup_type && (
                    <span className="scout-setup-badge">{scoutResult.setup_type}</span>
                  )}
                  {scoutResult.entry_price != null && (
                    <span className="scout-meta-chip">Entry {scoutResult.entry_price.toFixed(4)}</span>
                  )}
                  <span className="scout-meta-chip">
                    Confidence {Math.round(scoutResult.confidence * 100)}%
                  </span>
                </div>
                {scoutResult.metadata && (
                  <div className="advisor-result-stats">
                    {scoutResult.metadata.tools_used != null && <span>Tools: {scoutResult.metadata.tools_used}</span>}
                    {scoutResult.metadata.latency_ms != null && (
                      <span>Latency: {(scoutResult.metadata.latency_ms / 1000).toFixed(1)}s</span>
                    )}
                    {scoutResult.metadata.ran_at_utc && (
                      <span>Ran: {new Date(scoutResult.metadata.ran_at_utc).toLocaleString()}</span>
                    )}
                  </div>
                )}
              </header>

              <section className="scout-section">
                <h3 className="scout-section-title">Plain-English summary</h3>
                <div className="advisor-markdown">
                  <ReactMarkdown>{scoutResult.plain_english_summary}</ReactMarkdown>
                </div>
              </section>

              <section className="scout-section">
                <h3 className="scout-section-title">Technical reasoning</h3>
                <p className="scout-technical">{scoutResult.reasoning}</p>
              </section>
            </article>
          )}
        </div>
      )}

      {activeTab === 'structure' && <StructureChart />}
    </div>
  );
};

export default Advisor;
